import { Box, Stack, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import { useThrottle } from 'react-use';
import { DriveContentType } from 'src/@types/drive';
import { useDriveContentQuery } from 'src/hooks/useDriveContentQuery';
// import { DriveContentType } from 'src/@types/drive';
import {
  FileChangeViewButton,
  FileFilterName,
} from 'src/sections/@dashboard/drive';
import DriveMoveBreadcrumbs from 'src/sections/@dashboard/drive/shared/FileMoveDialog/DriveMoveBreadcrumbs';
import { EmptyFolder } from 'src/sections/@dashboard/drive/shared/FileMoveDialog/EmptyFolder';
import { useDriveAncestorsQuery } from 'src/sections/@dashboard/drive/shared/FileMoveDialog/useDriveAncestorsQuery';
import { useSearchContentQuery } from 'src/sections/@dashboard/drive/useSearchContentQuery';
import FileCloudCard from './cloudCards/FileFolderCloudCard';
import FileListCloudCard from './cloudCards/FileListCloudCard';

type Props = {
  isMedia: boolean;
};

export const CloudContent = ({ isMedia }: Props) => {
  const [view, setView] = useState<'list' | 'grid'>('grid');
  const [searchContent, setSearchContent] = useState<string>('');
  const [currentFolderId, setCurrentFolderId] = useState<string>('');
  const throttledSearchContent = useThrottle(searchContent, 500);

  const { driveContents } = useDriveContentQuery(currentFolderId);
  const { driveAncestors } = useDriveAncestorsQuery(currentFolderId);
  const { driveSearchContents } = useSearchContentQuery(throttledSearchContent);

  const contentData = useMemo(() => {
    const baseContents = throttledSearchContent.length
      ? driveSearchContents
      : driveContents;

    const filterContents = (contents: typeof baseContents) =>
      contents?.filter((item) => {
        if (isMedia) {
          return (
            item.type === DriveContentType.FOLDER ||
            !item.mimeType?.startsWith('application/')
          );
        }
        return (
          item.type === DriveContentType.FOLDER ||
          item.mimeType?.startsWith('application/')
        );
      });

    const sortContents = (contents: typeof baseContents) =>
      contents?.sort((a, b) => {
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;
        return a.name.localeCompare(b.name);
      });

    const filteredContents = filterContents(baseContents);
    return sortContents(filteredContents || []);
  }, [driveContents, isMedia, driveSearchContents, throttledSearchContent]);

  const handleChangeView = (
    event: React.MouseEvent<HTMLElement>,
    newView: 'list' | 'grid' | null,
  ) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContent(event.target.value);
  };

  const handleOpenFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
  };

  return (
    <Stack flexDirection="column" flexGrow={1} maxHeight="100%" overflow="auto">
      <CloudContentHeader>
        <FileFilterName
          filterName={searchContent}
          onFilterName={handleFilterName}
          onClearInput={() => setSearchContent('')}
          clearInput
        />
        <FileChangeViewButton onChange={handleChangeView} value={view} />
      </CloudContentHeader>
      <Stack
        direction="column"
        flexGrow={1}
        sx={{
          overflow: 'hidden',
          maxHeight: '100%',
        }}
      >
        {contentData?.length === 0 && <EmptyFolder />}
        {view === 'list' ? (
          <Stack sx={{ overflowY: 'auto' }} gap={1} p={1}>
            {contentData?.map((content) => (
              <FileListCloudCard
                key={content._id}
                content={content}
                handleOpenFolder={handleOpenFolder}
              />
            ))}
          </Stack>
        ) : (
          <Box
            gap={1}
            p={1}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
            sx={{ overflowY: 'auto' }}
          >
            {contentData?.map((content) => (
              <FileCloudCard
                key={content._id}
                content={content}
                handleOpenFolder={handleOpenFolder}
              />
            ))}
          </Box>
        )}
      </Stack>
      <Stack
        p={1}
        borderTop="1px solid"
        borderColor="divider"
        sx={{
          flexShrink: 0,
          width: '100%',
        }}
      >
        <DriveMoveBreadcrumbs
          action={handleOpenFolder}
          ancestors={!!currentFolderId ? driveAncestors : undefined}
        />
      </Stack>
    </Stack>
  );
};

const CloudContentHeader = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderTop: '1px solid',
  borderBottom: '1px solid',
  flexShrink: 0,
  borderColor: theme.palette.divider,
  '& > *': {
    maxHeight: 40,
  },
}));
